import config from "@/config";
import { BrandingHttpService } from "@/api/http/BrandingHttpService";
import { createGlobalState } from "@vueuse/core";
import { useReadonlyWithSetter } from "@/composables/common";
import type { Branding, BrandingColours } from "@/api/http/schemas/brandingSchemas";
import { computed, onMounted } from "vue";
import pick from "lodash/pick";
import { getObjectKeys } from "@/util/common";

export const DEFAULT_BRANDING: Readonly<BrandingColours> = {
  colour: "#272A40",
  button_colour: "#4476E5",
  button_text_colour: "#FFFFFF",
  heading_font_colour: "#FFFFFF",
  description_font_colour: "#7587A0",
  label_font_colour: "#545464",
  icon_colour: "#FFFFFF",
  progress_bar_colour: "#04FE72",
  content_title_color: "#FFFFFF",
};

export const cssVarToBrandingPropertyMap = new Map<string, keyof BrandingColours>([
  ["--branding-background", "colour"],
  ["--branding-icon", "icon_colour"],
  ["--branding-heading", "heading_font_colour"],
  ["--branding-label", "label_font_colour"],
  ["--branding-description", "description_font_colour"],
  ["--branding-button", "button_colour"],
  ["--branding-button-text", "button_text_colour"],
  ["--branding-progress-bar", "progress_bar_colour"],
  ["--branding-content-title", "content_title_color"],
]);

export const setBrandingFontFamily = (fontFamily: string | null): void => {
  const value = fontFamily ?? "Inter";
  document.documentElement.style.setProperty("--branding-font-family", `"${value}"`);
};

const useBrandingState = createGlobalState(() => {
  const [branding, setBranding] = useReadonlyWithSetter<Branding | null>(null);
  const [isBrandingLoading, setIsBrandingLoading] = useReadonlyWithSetter(false);

  const loadFont = (fontUrl: string) => {
    const existingLink = document.querySelector(`link[href="${fontUrl}"]`);
    if (existingLink) {
      return;
    }

    const link = document.createElement("link");
    link.rel = "stylesheet";
    link.href = fontUrl;
    document.head.appendChild(link);

    const url = new URL(fontUrl);
    const familyParam = url.searchParams.get("family");

    if (familyParam) {
      const fontFamily = familyParam.split(":")[0].replace(/\+/g, " ");
      setBrandingFontFamily(fontFamily);
    }
  };

  const applyBranding = <TBranding extends BrandingColours>(branding: TBranding) => {
    cssVarToBrandingPropertyMap.forEach((property, cssVar) => {
      document.documentElement.style.setProperty(cssVar, branding[property]);
    });
  };

  const brandingColours = computed(() => {
    if (!branding.value) {
      return Object.values(DEFAULT_BRANDING);
    }
    const colourProperties = getObjectKeys(DEFAULT_BRANDING);
    return Object.values(pick(branding.value, colourProperties));
  });

  const getBranding = async () => {
    if (!config.branding) {
      return;
    }
    setIsBrandingLoading(true);
    try {
      const data = await new BrandingHttpService().getBranding(config.branding);
      applyBranding(data);
      if (data.font_url !== null && data.font_url !== undefined) {
        loadFont(data.font_url);
      } else {
        setBrandingFontFamily(null);
      }
      setBranding(data);
    } finally {
      setIsBrandingLoading(false);
    }
  };

  onMounted(() => {
    applyBranding(DEFAULT_BRANDING);
  });

  return {
    branding,
    brandingColours,
    getBranding,
    applyBranding,
    setBranding,
    isBrandingLoading,
    setIsBrandingLoading,
  };
});

export default useBrandingState;
